import { Switch, Slider, Select, Typography, Space, Divider, Collapse, ColorPicker } from 'antd';
import useSceneStore from '../../stores/sceneStore';
import ParticlePanel from './ParticlePanel';

const { Text } = Typography;

function setPP(partial: Partial<{
  bloom: Partial<{ enabled: boolean; intensity: number; threshold: number; smoothing: number }>;
  dof: Partial<{ enabled: boolean; focusDistance: number; focalLength: number; aperture: number }>;
  grain: Partial<{ enabled: boolean; intensity: number }>;
  vignette: Partial<{ enabled: boolean; darkness: number; offset: number }>;
  ssao: Partial<{ enabled: boolean; intensity: number; radius: number; distance: number }>;
  antialiasing: Partial<{ enabled: boolean; mode: 'smaa' | 'fxaa' }>;
  outline: Partial<{ enabled: boolean; color: string; width: number }>;
}>) {
  const store = useSceneStore.getState();
  const pp = store.effects.postprocessing;
  useSceneStore.getState().setEffects({
    postprocessing: {
      ...pp,
      bloom: partial.bloom ? { ...pp.bloom, ...partial.bloom } : pp.bloom,
      dof: partial.dof ? { ...pp.dof, ...partial.dof } : pp.dof,
      grain: partial.grain ? { ...pp.grain, ...partial.grain } : pp.grain,
      vignette: partial.vignette ? { ...pp.vignette, ...partial.vignette } : pp.vignette,
      ssao: partial.ssao ? { ...pp.ssao, ...partial.ssao } : pp.ssao,
      antialiasing: partial.antialiasing ? { ...pp.antialiasing, ...partial.antialiasing } : pp.antialiasing,
      outline: partial.outline ? { ...pp.outline, ...partial.outline } : pp.outline,
    },
  });
}

export default function EffectsPanel() {
  const { effects, setEffects } = useSceneStore();
  const { dayNight, sky, clouds, weather, water, atmosphere, postprocessing } = effects;

  return (
    <div style={{ padding: '12px 16px' }}>
      <Text strong style={{ fontSize: 13 }}>视觉效果</Text>
      <Divider style={{ margin: '8px 0' }} />

      <Collapse
        ghost
        size="small"
        defaultActiveKey={['sky', 'weather', 'particles', 'post', 'lod']}
        items={[
          {
            key: 'sky',
            label: <Text style={{ fontSize: 12, fontWeight: 500 }}>天空 & 大气</Text>,
            children: (
              <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text style={{ fontSize: 12, color: '#888' }}>天空球</Text>
                    <Switch size="small" checked={sky.enabled}
                      onChange={(v) => setEffects({ sky: { ...sky, enabled: v } })} />
                  </Space>
                  <div style={{ marginTop: 4 }}>
                    <Text style={{ fontSize: 11, color: '#999' }}>太阳仰角: {(sky.sunElevation * 100).toFixed(0)}%</Text>
                    <Slider min={0} max={1} step={0.01} value={sky.sunElevation}
                      onChange={(v) => setEffects({ sky: { ...sky, sunElevation: v } })} />
                  </div>
                  <div>
                    <Text style={{ fontSize: 11, color: '#999' }}>太阳方位: {(sky.sunAzimuth * 100).toFixed(0)}%</Text>
                    <Slider min={0} max={1} step={0.01} value={sky.sunAzimuth}
                      onChange={(v) => setEffects({ sky: { ...sky, sunAzimuth: v } })} />
                  </div>
                </div>

                <div>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text style={{ fontSize: 12, color: '#888' }}>大气散射</Text>
                    <Switch size="small" checked={atmosphere.enabled}
                      onChange={(v) => setEffects({ atmosphere: { ...atmosphere, enabled: v } })} />
                  </Space>
                  {atmosphere.enabled && (
                    <>
                      <div style={{ marginTop: 4 }}>
                        <Text style={{ fontSize: 11, color: '#999' }}>强度: {(atmosphere.intensity * 100).toFixed(0)}%</Text>
                        <Slider min={0} max={1} step={0.01} value={atmosphere.intensity}
                          onChange={(v) => setEffects({ atmosphere: { ...atmosphere, intensity: v } })} />
                      </div>
                      <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text style={{ fontSize: 11, color: '#999' }}>颜色</Text>
                        <ColorPicker size="small" value={atmosphere.color}
                          onChange={(c) => setEffects({ atmosphere: { ...atmosphere, color: c.toHexString() } })} />
                      </div>
                    </>
                  )}
                </div>

                <div>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text style={{ fontSize: 12, color: '#888' }}>昼夜循环</Text>
                    <Switch size="small" checked={dayNight.enabled}
                      onChange={(v) => setEffects({ dayNight: { ...dayNight, enabled: v } })} />
                  </Space>
                  {dayNight.enabled && (
                    <>
                      <div style={{ marginTop: 4 }}>
                        <Text style={{ fontSize: 11, color: '#999' }}>时间: {dayNight.timeOfDay.toFixed(1)}h</Text>
                        <Slider min={0} max={24} step={0.1} value={dayNight.timeOfDay}
                          onChange={(v) => setEffects({ dayNight: { ...dayNight, timeOfDay: v } })} />
                      </div>
                      <div>
                        <Text style={{ fontSize: 11, color: '#999' }}>速度: {dayNight.speed.toFixed(2)}</Text>
                        <Slider min={0} max={1} step={0.01} value={dayNight.speed}
                          onChange={(v) => setEffects({ dayNight: { ...dayNight, speed: v } })} />
                      </div>
                    </>
                  )}
                </div>

                <div>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text style={{ fontSize: 12, color: '#888' }}>体积云</Text>
                    <Switch size="small" checked={clouds.enabled}
                      onChange={(v) => setEffects({ clouds: { ...clouds, enabled: v } })} />
                  </Space>
                  {clouds.enabled && (
                    <>
                      <div style={{ marginTop: 4 }}>
                        <Text style={{ fontSize: 11, color: '#999' }}>覆盖度: {(clouds.coverage * 100).toFixed(0)}%</Text>
                        <Slider min={0} max={1} step={0.01} value={clouds.coverage}
                          onChange={(v) => setEffects({ clouds: { ...clouds, coverage: v } })} />
                      </div>
                      <div>
                        <Text style={{ fontSize: 11, color: '#999' }}>密度: {(clouds.density * 100).toFixed(0)}%</Text>
                        <Slider min={0} max={1} step={0.01} value={clouds.density}
                          onChange={(v) => setEffects({ clouds: { ...clouds, density: v } })} />
                      </div>
                    </>
                  )}
                </div>
              </Space>
            ),
          },
          {
            key: 'weather',
            label: <Text style={{ fontSize: 12, fontWeight: 500 }}>天气 & 水面</Text>,
            children: (
              <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text style={{ fontSize: 12, color: '#888' }}>天气</Text>
                  </Space>
                  <div style={{ marginTop: 4 }}>
                    <Select
                      style={{ width: '100%' }}
                      size="small"
                      value={weather.type}
                      onChange={(v) => setEffects({ weather: { ...weather, type: v as 'clear' | 'rain' | 'snow' | 'cloudy' } })}
                      options={[
                        { value: 'clear', label: '晴朗' },
                        { value: 'cloudy', label: '多云' },
                        { value: 'rain', label: '雨天' },
                        { value: 'snow', label: '雪天' },
                      ]}
                    />
                  </div>
                  {weather.type !== 'clear' && (
                    <div style={{ marginTop: 4 }}>
                      <Text style={{ fontSize: 11, color: '#999' }}>强度: {(weather.intensity * 100).toFixed(0)}%</Text>
                      <Slider min={0} max={1} step={0.05} value={weather.intensity}
                        onChange={(v) => setEffects({ weather: { ...weather, intensity: v } })} />
                    </div>
                  )}
                </div>

                <div>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text style={{ fontSize: 12, color: '#888' }}>水面反射</Text>
                    <Switch size="small" checked={water.enabled}
                      onChange={(v) => setEffects({ water: { ...water, enabled: v } })} />
                  </Space>
                </div>
              </Space>
            ),
          },
          {
            key: 'particles',
            label: <Text style={{ fontSize: 12, fontWeight: 500 }}>粒子系统</Text>,
            children: <ParticlePanel />,
          },
          {
            key: 'lod',
            label: <Text style={{ fontSize: 12, fontWeight: 500 }}>LOD 细节层次</Text>,
            children: (
              <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                <Text style={{ fontSize: 11, color: '#999' }}>
                  LOD 自动根据相机距离切换模型细节级别，无需手动配置。
                </Text>
                <div style={{ fontSize: 11, color: '#888', lineHeight: 1.8 }}>
                  <div>距离 0-20：全细节（100%）</div>
                  <div>距离 20-50：80% 细节</div>
                  <div>距离 50-100：50% 细节</div>
                  <div>距离 100+：30% 细节</div>
                </div>
                <Text style={{ fontSize: 11, color: '#666' }}>
                  缩放相机远离模型可观察自动缩放效果。
                </Text>
              </Space>
            ),
          },
          {
            key: 'post',
            label: <Text style={{ fontSize: 12, fontWeight: 500 }}>后处理特效</Text>,
            children: (
              <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text style={{ fontSize: 12, color: '#888' }}>Bloom 泛光</Text>
                    <Switch size="small" checked={postprocessing.bloom.enabled}
                      onChange={(v) => setPP({ bloom: { enabled: v } })} />
                  </Space>
                  {postprocessing.bloom.enabled && (
                    <>
                      <div style={{ marginTop: 4 }}>
                        <Text style={{ fontSize: 11, color: '#999' }}>强度: {postprocessing.bloom.intensity.toFixed(1)}</Text>
                        <Slider min={0} max={3} step={0.05} value={postprocessing.bloom.intensity}
                          onChange={(v) => setPP({ bloom: { intensity: v } })} />
                      </div>
                      <div>
                        <Text style={{ fontSize: 11, color: '#999' }}>阈值: {postprocessing.bloom.threshold.toFixed(1)}</Text>
                        <Slider min={0} max={1} step={0.05} value={postprocessing.bloom.threshold}
                          onChange={(v) => setPP({ bloom: { threshold: v } })} />
                      </div>
                    </>
                  )}
                </div>

                <Divider style={{ margin: '4px 0' }} />

                <div>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text style={{ fontSize: 12, color: '#888' }}>DOF 景深</Text>
                    <Switch size="small" checked={postprocessing.dof.enabled}
                      onChange={(v) => setPP({ dof: { enabled: v } })} />
                  </Space>
                  {postprocessing.dof.enabled && (
                    <>
                      <div style={{ marginTop: 4 }}>
                        <Text style={{ fontSize: 11, color: '#999' }}>焦点距离: {postprocessing.dof.focusDistance.toFixed(1)}</Text>
                        <Slider min={0} max={20} step={0.1} value={postprocessing.dof.focusDistance}
                          onChange={(v) => setPP({ dof: { focusDistance: v } })} />
                      </div>
                      <div>
                        <Text style={{ fontSize: 11, color: '#999' }}>焦距: {postprocessing.dof.focalLength.toFixed(2)}</Text>
                        <Slider min={0} max={1} step={0.01} value={postprocessing.dof.focalLength}
                          onChange={(v) => setPP({ dof: { focalLength: v } })} />
                      </div>
                      <div>
                        <Text style={{ fontSize: 11, color: '#999' }}>光圈: {postprocessing.dof.aperture.toFixed(3)}</Text>
                        <Slider min={0} max={0.1} step={0.001} value={postprocessing.dof.aperture}
                          onChange={(v) => setPP({ dof: { aperture: v } })} />
                      </div>
                    </>
                  )}
                </div>

                <Divider style={{ margin: '4px 0' }} />

                <div>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text style={{ fontSize: 12, color: '#888' }}>胶片颗粒</Text>
                    <Switch size="small" checked={postprocessing.grain.enabled}
                      onChange={(v) => setPP({ grain: { enabled: v } })} />
                  </Space>
                  {postprocessing.grain.enabled && (
                    <div style={{ marginTop: 4 }}>
                      <Text style={{ fontSize: 11, color: '#999' }}>强度: {(postprocessing.grain.intensity * 100).toFixed(0)}%</Text>
                      <Slider min={0} max={0.2} step={0.005} value={postprocessing.grain.intensity}
                        onChange={(v) => setPP({ grain: { intensity: v } })} />
                    </div>
                  )}
                </div>

                <Divider style={{ margin: '4px 0' }} />

                <div>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text style={{ fontSize: 12, color: '#888' }}>暗角</Text>
                    <Switch size="small" checked={postprocessing.vignette.enabled}
                      onChange={(v) => setPP({ vignette: { enabled: v } })} />
                  </Space>
                  {postprocessing.vignette.enabled && (
                    <>
                      <div style={{ marginTop: 4 }}>
                        <Text style={{ fontSize: 11, color: '#999' }}>暗度: {(postprocessing.vignette.darkness * 100).toFixed(0)}%</Text>
                        <Slider min={0} max={1} step={0.05} value={postprocessing.vignette.darkness}
                          onChange={(v) => setPP({ vignette: { darkness: v } })} />
                      </div>
                      <div>
                        <Text style={{ fontSize: 11, color: '#999' }}>偏移: {(postprocessing.vignette.offset * 100).toFixed(0)}%</Text>
                        <Slider min={0} max={1} step={0.05} value={postprocessing.vignette.offset}
                          onChange={(v) => setPP({ vignette: { offset: v } })} />
                      </div>
                    </>
                  )}
                </div>

                <Divider style={{ margin: '4px 0' }} />

                <div>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text style={{ fontSize: 12, color: '#888' }}>SSAO 环境光遮蔽</Text>
                    <Switch size="small" checked={postprocessing.ssao.enabled}
                      onChange={(v) => setPP({ ssao: { enabled: v } })} />
                  </Space>
                  {postprocessing.ssao.enabled && (
                    <>
                      <div style={{ marginTop: 4 }}>
                        <Text style={{ fontSize: 11, color: '#999' }}>强度: {postprocessing.ssao.intensity.toFixed(2)}</Text>
                        <Slider min={0} max={3} step={0.1} value={postprocessing.ssao.intensity}
                          onChange={(v) => setPP({ ssao: { intensity: v } })} />
                      </div>
                      <div>
                        <Text style={{ fontSize: 11, color: '#999' }}>半径: {postprocessing.ssao.radius.toFixed(2)}</Text>
                        <Slider min={0} max={2} step={0.05} value={postprocessing.ssao.radius}
                          onChange={(v) => setPP({ ssao: { radius: v } })} />
                      </div>
                      <div>
                        <Text style={{ fontSize: 11, color: '#999' }}>距离: {postprocessing.ssao.distance.toFixed(2)}</Text>
                        <Slider min={0} max={2} step={0.05} value={postprocessing.ssao.distance}
                          onChange={(v) => setPP({ ssao: { distance: v } })} />
                      </div>
                    </>
                  )}
                </div>

                <Divider style={{ margin: '4px 0' }} />

                <div>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text style={{ fontSize: 12, color: '#888' }}>抗锯齿</Text>
                    <Switch size="small" checked={postprocessing.antialiasing.enabled}
                      onChange={(v) => setPP({ antialiasing: { enabled: v } })} />
                  </Space>
                  {postprocessing.antialiasing.enabled && (
                    <div style={{ marginTop: 4 }}>
                      <Select
                        size="small"
                        style={{ width: '100%' }}
                        value={postprocessing.antialiasing.mode}
                        onChange={(v) => setPP({ antialiasing: { mode: v } })}
                        options={[
                          { value: 'smaa', label: 'SMAA (高质量)' },
                          { value: 'fxaa', label: 'FXAA (高性能)' },
                        ]}
                      />
                    </div>
                  )}
                </div>

                <Divider style={{ margin: '4px 0' }} />

                <div>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text style={{ fontSize: 12, color: '#888' }}>选中描边</Text>
                    <Switch size="small" checked={postprocessing.outline.enabled}
                      onChange={(v) => setPP({ outline: { enabled: v } })} />
                  </Space>
                  {postprocessing.outline.enabled && (
                    <>
                      <div style={{ marginTop: 4 }}>
                        <Text style={{ fontSize: 11, color: '#999' }}>宽度: {postprocessing.outline.width}</Text>
                        <Slider min={1} max={10} step={1} value={postprocessing.outline.width}
                          onChange={(v) => setPP({ outline: { width: v } })} />
                      </div>
                      <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text style={{ fontSize: 11, color: '#999' }}>颜色</Text>
                        <ColorPicker size="small" value={postprocessing.outline.color}
                          onChange={(c) => setPP({ outline: { color: c.toHexString() } })} />
                      </div>
                    </>
                  )}
                </div>
              </Space>
            ),
          },
        ]}
      />
    </div>
  );
}
